import * as React from "react";
import {
  ArrowLeft, BarChart3, ChevronRight, Search, Loader2, TrendingUp,
  TrendingDown, Minus, Eye, HelpCircle, Info
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import axios from "axios";
import { toast } from "sonner";
import { trackActivity } from "@/utils/trackActivity";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const HelpTip = ({ text, side = "top" }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild>
      <HelpCircle className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline-block ml-1" />
    </TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed bg-slate-900 text-slate-100 px-3 py-2">
      {text}
    </TooltipContent>
  </Tooltip>
);

const RATING_STYLES = {
  "Top 10%": { color: "text-emerald-700", bg: "bg-emerald-100", icon: "up" },
  "Top 25%": { color: "text-emerald-600", bg: "bg-emerald-50", icon: "up" },
  "Top 50%": { color: "text-amber-600", bg: "bg-amber-50", icon: "flat" },
  "Bottom 50%": { color: "text-orange-600", bg: "bg-orange-50", icon: "down" },
  "Bottom 25%": { color: "text-red-600", bg: "bg-red-100", icon: "down" },
};

const RatingBadge = ({ rating }) => {
  const s = RATING_STYLES[rating] || { color: "text-slate-600", bg: "bg-slate-100" };
  return <Badge className={`text-xs ${s.bg} ${s.color}`}>{rating}</Badge>;
};

const PercentileBar = ({ percentile, p25 = 25, p50 = 50, p75 = 75 }) => {
  const pct = Math.min(Math.max(percentile, 0), 100);
  const color = pct >= 75 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : pct >= 25 ? "bg-orange-500" : "bg-red-500";
  return (
    <div className="relative w-full h-3 bg-slate-100 rounded-full overflow-hidden">
      <div className={`absolute inset-y-0 left-0 ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      <div className="absolute inset-y-0 border-l border-orange-300 border-dashed" style={{ left: "25%" }} />
      <div className="absolute inset-y-0 border-l border-amber-400 border-dashed" style={{ left: "50%" }} />
      <div className="absolute inset-y-0 border-l border-emerald-400 border-dashed" style={{ left: "75%" }} />
    </div>
  );
};

const METRIC_TIPS = {
  spend_usd: "Gasto total en USD. Percentiles bajos = gastando menos que lo habitual.",
  clicks: "Total de clics. Percentiles bajos pueden indicar problemas de relevancia.",
  impressions: "Impresiones totales. Cambios reflejan presupuesto o alcance de audiencia.",
  conversions: "Conversiones completadas. La métrica más importante para el negocio.",
  ctr: "Click-Through Rate. Mide relevancia del anuncio.",
  conversion_rate: "Tasa de conversión. Mide efectividad del landing page.",
  cpa: "Costo Por Adquisición. Menor = más eficiente.",
  cpm: "Costo Por Mil impresiones. Indica competitividad de la subasta.",
};

// === CLIENT LIST ===
const ClientList = ({ onSelect }) => {
  const [clients, setClients] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [detectionDate, setDetectionDate] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/percentile-v2/clients`, { withCredentials: true });
        setClients(res.data.clients || []);
        setDetectionDate(res.data.detection_date);
        trackActivity("view_module", "percentile_v2");
      } catch (e) { toast.error("Error al cargar clientes"); }
      finally { setIsLoading(false); }
    })();
  }, []);

  const filtered = clients.filter(c => c.cliente.toLowerCase().includes(search.toLowerCase()));

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6">
      <div className="p-4 rounded-lg bg-cyan-50/60 border border-cyan-100 flex gap-3">
        <Info className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-slate-600 space-y-1">
          <p className="font-medium text-slate-700">¿Cómo funciona?</p>
          <p>Compara el rendimiento de <strong>ayer</strong> contra los <strong>percentiles históricos</strong> de los últimos 60 días, segmentado por día de la semana. Responde: <strong>¿fue un día bueno o malo?</strong> para cada métrica.</p>
        </div>
      </div>
      <div>
        <h2 className="text-2xl font-bold text-slate-800 inline-flex items-center gap-1">
          Selecciona un Cliente
          <HelpTip text="Cada tarjeta muestra cuántas métricas cayeron por debajo o por encima de lo normal." side="bottom" />
        </h2>
        <p className="text-slate-500 text-sm mt-1">Datos del {detectionDate || "último análisis"} · {clients.length} clientes</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(c => {
          const total = c.total_results || 1;
          const belowMedian = (c.bottom_25 || 0) + (c.bottom_50 || 0);
          const aboveP75 = (c.top_10 || 0) + (c.top_25 || 0);
          return (
            <Card key={c.cliente} className={`cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 ${c.bottom_25 > 0 ? "border-red-200" : c.bottom_50 > 0 ? "border-orange-200" : ""}`}
              onClick={() => { trackActivity("select_client", "percentile_v2", { cliente: c.cliente }); onSelect(c.cliente); }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-800">{c.cliente}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{c.accounts} cuentas · {total} métricas</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {c.bottom_25 > 0 && <Badge className="bg-red-100 text-red-700 text-[10px]">{c.bottom_25} Bottom 25%</Badge>}
                  {c.bottom_50 > 0 && <Badge className="bg-orange-100 text-orange-600 text-[10px]">{c.bottom_50} Bottom 50%</Badge>}
                  {c.top_10 > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{c.top_10} Top 10%</Badge>}
                  {belowMedian === 0 && aboveP75 === 0 && <Badge className="bg-slate-100 text-slate-600 text-[10px]">Dentro de lo esperado</Badge>}
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] text-slate-400 mb-0.5">
                    <span>{belowMedian} bajo mediana</span>
                    <span>{aboveP75} sobre P75</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden flex">
                    {c.bottom_25 > 0 && <div className="bg-red-400 h-full" style={{ width: `${(c.bottom_25/total)*100}%` }} />}
                    {c.bottom_50 > 0 && <div className="bg-orange-300 h-full" style={{ width: `${(c.bottom_50/total)*100}%` }} />}
                    {c.top_50 > 0 && <div className="bg-amber-200 h-full" style={{ width: `${(c.top_50/total)*100}%` }} />}
                    {c.top_25 > 0 && <div className="bg-emerald-300 h-full" style={{ width: `${(c.top_25/total)*100}%` }} />}
                    {c.top_10 > 0 && <div className="bg-emerald-500 h-full" style={{ width: `${(c.top_10/total)*100}%` }} />}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && <p className="text-slate-400 col-span-3 text-center py-8">No se encontraron clientes</p>}
      </div>
    </div>
  );
};

// === CLIENT DASHBOARD ===
const ClientDashboard = ({ cliente, onBack, onSelectAccount }) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/percentile-v2/dashboard/${encodeURIComponent(cliente)}`, { withCredentials: true });
        setData(res.data);
      } catch (e) { toast.error("Error al cargar dashboard"); }
      finally { setIsLoading(false); }
    })();
  }, [cliente]);

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return null;

  const { summary, by_metric, accounts } = data;
  const ratings = ["Top 10%", "Top 25%", "Top 50%", "Bottom 50%", "Bottom 25%"];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Clientes</Button>
        <div className="h-5 w-px bg-slate-200" />
        <div>
          <h2 className="text-xl font-bold text-slate-800">{cliente}</h2>
          <p className="text-xs text-slate-400">Análisis del {data.detection_date}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {ratings.map(r => {
          const s = RATING_STYLES[r];
          return (
            <Card key={r} className={summary[r] > 0 && r.includes("Bottom") ? "border-red-200" : ""}>
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{summary[r] || 0}</p>
                <p className="text-[10px] text-slate-500">{r}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1">
            Distribución por Métrica <HelpTip text="Cuántas cuentas caen en cada segmento de percentil para cada métrica." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {by_metric.map(m => (
              <div key={m.metric_name} className="p-3 rounded-lg border bg-slate-50">
                <p className="text-sm font-medium text-slate-700 inline-flex items-center gap-0.5">
                  {m.metric_name}{METRIC_TIPS[m.metric_name] && <HelpTip text={METRIC_TIPS[m.metric_name]} />}
                </p>
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  {m.top_10 > 0 && <span className="text-[10px] text-emerald-600 font-bold">{m.top_10}★</span>}
                  {m.bottom_25 > 0 && <span className="text-[10px] text-red-600 font-bold">{m.bottom_25}▼</span>}
                  {m.bottom_50 > 0 && <span className="text-[10px] text-orange-600">{m.bottom_50}↓</span>}
                  {!m.top_10 && !m.bottom_25 && !m.bottom_50 && <span className="text-[10px] text-slate-400">Normal</span>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Cuentas</CardTitle>
          <CardDescription>Ordenadas por percentil promedio (más bajo primero). Clic para detalle.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-[35%]">Cuenta</TableHead>
              <TableHead className="w-[12%]">Plataforma</TableHead>
              <TableHead className="w-[13%] text-center">Bajo Mediana</TableHead>
              <TableHead className="w-[13%] text-center">Sobre P75</TableHead>
              <TableHead className="w-[17%] text-right">Percentil Prom.</TableHead>
              <TableHead className="w-[10%]"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {accounts.map(acc => (
                <TableRow key={acc.account_id} className="cursor-pointer hover:bg-slate-50" onClick={() => onSelectAccount(acc.account_id)}>
                  <TableCell>
                    <p className="font-medium text-sm text-slate-800">{acc.account_name}</p>
                    <p className="text-[10px] text-slate-400">{acc.account_id}</p>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{acc.platform}</Badge></TableCell>
                  <TableCell className="text-center">
                    {acc.below_median > 0 ? <span className="text-red-600 font-bold">{acc.below_median}</span> : <span className="text-slate-300">0</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {acc.above_p75 > 0 ? <span className="text-emerald-600 font-bold">{acc.above_p75}</span> : <span className="text-slate-300">0</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono text-sm font-bold ${acc.avg_percentile >= 75 ? "text-emerald-600" : acc.avg_percentile >= 50 ? "text-amber-600" : acc.avg_percentile >= 25 ? "text-orange-600" : "text-red-600"}`}>
                      P{acc.avg_percentile}
                    </span>
                  </TableCell>
                  <TableCell><Eye className="h-4 w-4 text-slate-300" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// === ACCOUNT DETAIL ===
const AccountDetail = ({ accountId, onBack }) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/percentile-v2/account/${accountId}`, { withCredentials: true });
        setData(res.data);
      } catch (e) { toast.error("Error al cargar detalle"); }
      finally { setIsLoading(false); }
    })();
  }, [accountId]);

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data || !data.metrics.length) return null;

  const { account, metrics } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Dashboard</Button>
        <div className="h-5 w-px bg-slate-200" />
        <div>
          <h2 className="text-xl font-bold text-slate-800">{account.account_name}</h2>
          <div className="flex gap-3 text-xs text-slate-400 mt-0.5">
            <span>{account.platform}</span><span>{account.cliente}</span>
            {account.pais && <span>{account.pais}</span>}
            <span>Análisis: {account.detection_date}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
        {["Top 10%", "Top 25%", "Top 50%", "Bottom 50%", "Bottom 25%"].map(r => {
          const count = metrics.filter(m => m.rating === r).length;
          const s = RATING_STYLES[r];
          return (
            <Card key={r}>
              <CardContent className="p-3 text-center">
                <p className={`text-xl font-bold ${s.color}`}>{count}</p>
                <p className="text-[10px] text-slate-500">{r}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1">
            Detalle por Métrica
            <HelpTip text="Cada tarjeta muestra dónde cayó el valor de ayer dentro de la distribución histórica de los últimos 60 días para el mismo día de la semana." />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {metrics.map(m => {
            const s = RATING_STYLES[m.rating] || {};
            return (
              <div key={m.metric_name} className={`p-4 rounded-lg border ${s.bg || "bg-slate-50"} ${m.rating?.includes("Bottom") ? "border-red-200" : "border-slate-200"}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-slate-800 inline-flex items-center gap-0.5">
                      {m.metric_name}{METRIC_TIPS[m.metric_name] && <HelpTip text={METRIC_TIPS[m.metric_name]} />}
                    </span>
                    <RatingBadge rating={m.rating} />
                  </div>
                  <span className={`text-sm font-bold font-mono ${s.color || "text-slate-600"}`}>P{m.percentile_rank}</span>
                </div>
                <div className="grid grid-cols-6 gap-3 text-xs mt-2">
                  <div>
                    <p className="text-slate-500">Real</p>
                    <p className="font-bold text-slate-800 text-sm">{m.actual_value?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">P10</p>
                    <p className="text-slate-600">{m.p10?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">P25</p>
                    <p className="text-slate-600">{m.p25?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 font-medium">Mediana</p>
                    <p className="text-slate-700 font-medium">{m.p50?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">P75</p>
                    <p className="text-slate-600">{m.p75?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">P90</p>
                    <p className="text-slate-600">{m.p90?.toLocaleString()}</p>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex-1"><PercentileBar percentile={m.percentile_rank} /></div>
                  <span className={`text-xs font-mono ${m.vs_median_pct > 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {m.vs_median_pct > 0 ? "+" : ""}{m.vs_median_pct}% vs mediana
                  </span>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

// === MAIN VIEW ===
export const PercentileAnalysisV2View = ({ onBack }) => {
  const [view, setView] = React.useState("clients");
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [selectedAccount, setSelectedAccount] = React.useState(null);

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/30">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Volver</Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500">
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 inline-flex items-center gap-1">
                  Análisis de Percentiles v2
                  <HelpTip text="Compara el rendimiento de ayer contra la distribución histórica. ¿Fue un día bueno o malo para cada cuenta y métrica?" side="bottom" />
                </h1>
                <p className="text-xs text-slate-500">¿Día bueno o malo? — Basado en historial por día de la semana</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {view === "clients" && <ClientList onSelect={(c) => { setSelectedClient(c); setView("dashboard"); }} />}
        {view === "dashboard" && <ClientDashboard cliente={selectedClient} onBack={() => { setSelectedClient(null); setView("clients"); }} onSelectAccount={(id) => { setSelectedAccount(id); setView("detail"); }} />}
        {view === "detail" && <AccountDetail accountId={selectedAccount} onBack={() => { setSelectedAccount(null); setView("dashboard"); }} />}
      </main>
    </div>
    </TooltipProvider>
  );
};
